import { useEffect } from "react";
import eventBus from "@sd-utility/eventbus";
import { captureAPMError } from "client/components/utils/captureError";

// Import UVBasketExist properly or mock it
import { UVBasketExist } from "./helpers";

export interface OdmDataType {
  s4l: boolean;
  basketCount: string;
  myOffersCount: string;
}

interface UVBasketItem {
  quantity: number;
  product?: {
    warranty_price?: number;
  };
}

export let ENABLE_LEGACY_BASKET = true;

export const setEnableLegacyBasket = (value: boolean) => {
  ENABLE_LEGACY_BASKET = value;
};

const MY_OFFERS_POLL_INTERVAL_MS = 60 * 60 * 1000; // 1 hour

export const fetchS4LCount = async (): Promise<{ s4lCount: boolean }> => {
  if (window.ODM?.s4l?.getList) {
    const { data } = await window.ODM.s4l.getList();

    return { s4lCount: data?.items.length > 0 };
  } else {
    console.error("ODM.s4l.getList is not available");
  }
  return { s4lCount: false };
};

const normaliseMyOffersCount = (value: unknown): string => {
  // ODM.myOffers.getCount() can return a number or { count } (depending on integration).
  if (typeof value === "number") {
    return Number.isFinite(value) && value > 0
      ? String(Math.floor(value))
      : "0";
  }

  if (
    value &&
    typeof value === "object" &&
    "count" in value &&
    typeof (value as { count?: unknown }).count === "number"
  ) {
    const n = (value as { count: number }).count;
    return Number.isFinite(n) && n > 0 ? String(Math.floor(n)) : "0";
  }

  return "0";
};

export const fetchMyOffersCount = async (): Promise<{
  myOffersCount: string;
}> => {
  if (window.ODM?.myOffers?.getCount) {
    const response = await window.ODM.myOffers.getCount();
    return { myOffersCount: normaliseMyOffersCount(response) };
  } else {
    console.error("ODM.myOffers.getCount is not available");
  }

  return { myOffersCount: "0" };
};

export const fetchBasketCount = async (): Promise<{ basketCount: string }> => {
  if (!ENABLE_LEGACY_BASKET) {
    if (window.ODM?.basket?.getCount) {
      const response = await window.ODM.basket.getCount();
      if (response.status === 200) {
        return { basketCount: String(response.data ?? 0) };
      } else {
        console.error(`Unexpected status: ${response.status}`);
      }
    } else {
      console.error("ODM.basket.getCount is not available");
    }
    return { basketCount: "0" };
  } else {
    let uvItems: UVBasketItem[] = [];
    let basketSize = 0;

    if (window.universal_variable?.basket?.line_items) {
      uvItems = window.universal_variable.basket.line_items;
    }

    if (window.tvgThinDataLayer?.basketCount) {
      basketSize = window.tvgThinDataLayer.basketCount;
    } else if (UVBasketExist()) {
      // use the imported helper
      uvItems.forEach((item) => {
        const qty = item.quantity;
        basketSize += item.product?.warranty_price ? qty * 2 : qty;
      });
    }

    return { basketCount: String(basketSize) };
  }
};

export const useODMEffect = (
  setOdmData: React.Dispatch<React.SetStateAction<OdmDataType>>,
  options?: { shouldFetchMyOffersCount?: boolean },
) => {
  useEffect(() => {
    let abortController: AbortController | undefined;

    (async () => {
      try {
        const { s4lCount } = await fetchS4LCount();
        const { basketCount } = await fetchBasketCount();

        const shouldFetchMyOffersCount =
          options?.shouldFetchMyOffersCount ?? false;
        const { myOffersCount } = shouldFetchMyOffersCount
          ? await fetchMyOffersCount()
          : { myOffersCount: "0" };

        setOdmData({ s4l: s4lCount, basketCount, myOffersCount });
      } catch (err) {
        captureAPMError(err);
      }
    })();

    const shouldPollMyOffersCount = options?.shouldFetchMyOffersCount ?? false;
    if (!shouldPollMyOffersCount) return;

    const pollMyOffersCount = async () => {
      if (typeof document !== "undefined") {
        if (document.visibilityState && document.visibilityState !== "visible")
          return;
      }

      if (!window.ODM?.myOffers?.getCount) return;

      abortController?.abort();
      abortController = new AbortController();

      try {
        const { myOffersCount } = await fetchMyOffersCount();
        setOdmData((prev) => ({ ...prev, myOffersCount }));
      } catch (err) {
        captureAPMError(err);
        setOdmData((prev) => prev);
      }
    };

    // initial fetch already done above; poll every hour after that
    const intervalId =
      process.env.NODE_ENV === "test"
        ? null
        : window.setInterval(pollMyOffersCount, MY_OFFERS_POLL_INTERVAL_MS);

    const handleVisibilityChange = () => {
      // If the user returns after being away for a while, refresh eagerly.
      if (document.visibilityState === "visible") {
        pollMyOffersCount();
      }
    };

    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      abortController?.abort();
      if (typeof document !== "undefined") {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange,
        );
      }
    };
  }, [setOdmData, options?.shouldFetchMyOffersCount]);
};

export const useODMSubEffect = (
  setOdmData: React.Dispatch<React.SetStateAction<OdmDataType>>,
  odmData: OdmDataType,
) => {
  useEffect(() => {
    const basketSub = eventBus.subscribe("cart/count", (count: number) => {
      setOdmData((prev) => ({ ...prev, basketCount: String(count) }));
    });

    const s4lSub = eventBus.subscribe("s4l/hasItems", (value: unknown) => {
      setOdmData((prev) => ({ ...prev, s4l: value as boolean }));
    });

    const basketSubPrd = eventBus.subscribe("basket/add", () => {
      setOdmData((prev) => ({
        ...prev,
        basketCount: (Number(prev.basketCount) + 1).toString(),
      }));
    });

    // When the customer visits the My Offers page, the SPA publishes the count
    // so the header badge updates immediately (they have seen the offers).
    const myOffersCountSub = eventBus.subscribe(
      "my-offers/count",
      (payload: unknown) => {
        const count =
          typeof payload === "number" &&
          Number.isFinite(payload) &&
          payload >= 0
            ? Math.floor(payload)
            : 0;
        setOdmData((prev) => ({ ...prev, myOffersCount: String(count) }));
      },
    );

    return () => {
      basketSub.unsubscribe();
      s4lSub.unsubscribe();
      basketSubPrd.unsubscribe();
      myOffersCountSub.unsubscribe();
    };
  }, [setOdmData, odmData.s4l, odmData.basketCount]);
};
